import { Flex, TextInput, TextInputProps } from "@gravity-ui/uikit";
import { useState } from "react";
import { Control, useController } from "react-hook-form";
import { useTranslate } from "src/i18n";

import { RegFormInput } from "../../RegistrationPage.types";
import css from "./PasswordField.module.scss";
import { PasswordStrength } from "./widgets/PasswordStrength";

interface PasswordFieldProps {
  name: keyof RegFormInput;
  control: Control<RegFormInput>;
}

const DEFAULT_VALIDATE = {
  minLength: false,
  uppercase: false,
  lowercase: false,
  number: false,
  specialChar: false,
};

export const PasswordField = ({
  name,
  control,
  ...props
}: PasswordFieldProps & TextInputProps) => {
  const { t } = useTranslate();

  const validatePassword = (password?: string) => {
    if (!password) {
      setPasswordConditions(DEFAULT_VALIDATE);
      return false;
    }

    // NOTE: сама регулярка  /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}$/,
    const conditions = {
      minLength: password.length >= 8 && password.length <= 20,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      specialChar: /[!@#$%^&*(),.?"':{}|<>~[\];`=+/-]/.test(password),
    };

    setPasswordConditions(conditions);

    return Object.values(conditions).every((cond) => cond);
  };

  const {
    field: { onChange, onBlur, value, ref },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: {
      required: "Обязательное поле",
      validate: () => passwordConditions.minLength,
      minLength: { value: 8, message: "Минимальная длина 8" },
    },
  });

  const [passwordConditions, setPasswordConditions] =
    useState(DEFAULT_VALIDATE);

  const stringValue = typeof value === "string" ? value : "";

  return (
    <Flex direction="column" gap="2" className={css.passwordField}>
      <TextInput
        placeholder={t(
          "registration.placeholder.password",
          "минимум 8 символов",
        )}
        ref={ref}
        value={stringValue}
        onBlur={onBlur}
        size="xl"
        type="password"
        errorPlacement="outside"
        validationState={
          stringValue && !passwordConditions.minLength ? "invalid" : undefined
        }
        errorMessage={
          error ? (
            <ul>
              <li
                className={
                  passwordConditions.minLength ? css.valid : css.invalid
                }
              >
                {t(
                  "registration.password.length_requirement",
                  "От 8 до 20 символов",
                )}
              </li>
            </ul>
          ) : (
            ""
          )
        }
        {...props}
        onChange={(e) => {
          onChange(e);
          validatePassword(e.target.value);
          props.onChange?.(e);
        }}
      />
      <PasswordStrength
        conditions={passwordConditions}
        password={stringValue}
      />
    </Flex>
  );
};
